import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { TripNote } from "@poi-planner/shared";
import { useUIStore } from "../store/uiStore";
import { useTripStore } from "../store/tripStore";
import { useLibraryStore } from "../store/libraryStore";
import { useLegStore } from "../store/legStore";
import { legPalette } from "../theme/colors";
import { Modal } from "./Modal";
import { Button } from "./Button";
import {
  FormField,
  FormInput,
  FormRow,
  FormSelect,
  FormTextarea,
} from "./FormFields";
import { EventEditorAlert, EventEditorSection } from "./EventEditorSection";
import { NoteTagPicker } from "./NoteTagPicker";
import { epochMs } from "../utils/zonedTime";
import {
  TimezoneChangeNotice,
  EventTimingFields,
  eventTimeFieldsError,
  eventTimeFieldsFromZoned,
  zonedFromEventTimeFields,
  type EventTimeFieldsValue,
} from "./EventTimingFields";

function emptyTimeFields(zone: string): EventTimeFieldsValue {
  return { date: "", time: "", zone, offset: null };
}

/**
 * Leg membership for a note. Unlike POI membership — which is granular and
 * assigned by dragging a place onto a bar — a note's `legIds` rides inside the
 * full-trip snapshot, so this writes through the normal updateNote/autosave
 * path along with every other field on the note.
 */
function NoteLegChips({
  selectedIds,
  onChange,
  disabled,
}: {
  selectedIds: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
}) {
  const legs = useLegStore((state) => state.legs);

  if (legs.length === 0) {
    return (
      <p className="text-xs italic text-tertiary">
        This trip has no legs yet. Add one from the calendar&apos;s all-day row.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {legs.map((leg) => {
        const selected = selectedIds.includes(leg.id);
        const palette = legPalette(leg.color);
        return (
          <Button
            key={leg.id}
            variant="unstyled"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() =>
              onChange(
                selected
                  ? selectedIds.filter((id) => id !== leg.id)
                  : [...selectedIds, leg.id],
              )
            }
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-all focus-visible:outline-2 focus-visible:outline-offset-2",
              disabled
                ? "cursor-default"
                : "cursor-pointer hover:brightness-95",
              !selected && "bg-surface",
            )}
            // Runtime palette values: each leg carries its own colour index.
            style={
              selected
                ? {
                    backgroundColor: palette.bg,
                    borderColor: palette.border,
                    color: palette.text,
                  }
                : { borderColor: palette.border }
            }
          >
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: palette.dot }}
            />
            {leg.name}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * Create/edit scheduled trip notes — the replacement for the kanban's
 * "Add Transport" flow. Transport is a note tagged with a mode; start/end
 * POI refs give the route subtitle and keep travel estimates working.
 * Start/end zones are editable independently (flights).
 */
export function NoteEditorModal() {
  const activeEditor = useUIStore((s) => s.activeEditor);
  const noteDraftTiming = useUIStore((s) => s.noteDraftTiming);
  const timingProposal = useUIStore((s) => s.timingProposal);
  const closeEditor = useUIStore((s) => s.closeEditor);

  const notes = useTripStore((s) => s.schedule.notes);
  const trip = useTripStore((s) => s.trip);
  const createNote = useTripStore((s) => s.createNote);
  const updateNote = useTripStore((s) => s.updateNote);
  const deleteNote = useTripStore((s) => s.deleteNote);
  const isReadOnly = useTripStore((s) => s.isReadOnly);

  const tags = useLibraryStore((s) => s.tags);
  const pois = useLibraryStore((s) => s.pois);

  const isOpen = activeEditor?.type === "note";
  const isCreate = isOpen && activeEditor.mode === "create";
  const editingNote: TripNote | undefined =
    isOpen && activeEditor.entityId
      ? notes.find((note) => note.id === activeEditor.entityId)
      : undefined;

  const defaultZone = trip?.defaultTimezone ?? "UTC";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [legIds, setLegIds] = useState<string[]>([]);
  const [startPoiId, setStartPoiId] = useState("");
  const [endPoiId, setEndPoiId] = useState("");
  const [locked, setLocked] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [allDayStartDate, setAllDayStartDate] = useState("");
  const [allDayEndDate, setAllDayEndDate] = useState("");
  const [start, setStart] = useState<EventTimeFieldsValue>(() =>
    emptyTimeFields(defaultZone),
  );
  const [end, setEnd] = useState<EventTimeFieldsValue>(() =>
    emptyTimeFields(defaultZone),
  );
  const timedDraftBeforeAllDay = useRef<{
    start: EventTimeFieldsValue;
    end: EventTimeFieldsValue;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // (Re)hydrate whenever the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (editingNote) {
      setName(editingNote.name);
      setDescription(editingNote.description ?? "");
      setTagIds(editingNote.tagIds);
      setLegIds(editingNote.legIds ?? []);
      setStartPoiId(editingNote.startPoiId ?? "");
      setEndPoiId(editingNote.endPoiId ?? "");
      setLocked(editingNote.locked);
      setAllDay(editingNote.allDayStartDate !== null);
      setAllDayStartDate(editingNote.allDayStartDate ?? "");
      setAllDayEndDate(editingNote.allDayEndDate ?? "");
      const proposal =
        timingProposal?.eventType === "note" &&
        timingProposal.entityId === editingNote.id
          ? timingProposal
          : null;
      const startTime = proposal?.startTime ?? editingNote.startTime;
      const endTime = proposal?.endTime ?? editingNote.endTime;
      const nextStart = startTime
        ? eventTimeFieldsFromZoned(startTime)
        : emptyTimeFields(defaultZone);
      const nextEnd = endTime
        ? eventTimeFieldsFromZoned(endTime)
        : emptyTimeFields(defaultZone);
      setStart(nextStart);
      setEnd(nextEnd);
      timedDraftBeforeAllDay.current =
        startTime && endTime ? { start: nextStart, end: nextEnd } : null;
    } else {
      setName("");
      setDescription("");
      setTagIds([]);
      setLegIds([]);
      setStartPoiId("");
      setEndPoiId("");
      setLocked(false);
      setAllDay(noteDraftTiming?.type === "all-day");
      setAllDayStartDate(
        noteDraftTiming?.type === "all-day" ? noteDraftTiming.startDate : "",
      );
      setAllDayEndDate(
        noteDraftTiming?.type === "all-day" ? noteDraftTiming.endDate : "",
      );
      const nextStart =
        noteDraftTiming?.type === "timed"
          ? eventTimeFieldsFromZoned(noteDraftTiming.startTime)
          : emptyTimeFields(defaultZone);
      const nextEnd =
        noteDraftTiming?.type === "timed"
          ? eventTimeFieldsFromZoned(noteDraftTiming.endTime)
          : emptyTimeFields(defaultZone);
      setStart(nextStart);
      setEnd(nextEnd);
      timedDraftBeforeAllDay.current =
        noteDraftTiming?.type === "timed"
          ? { start: nextStart, end: nextEnd }
          : null;
    }
  }, [defaultZone, editingNote, isOpen, noteDraftTiming, timingProposal]);

  const activeTimingProposal =
    editingNote &&
    timingProposal?.eventType === "note" &&
    timingProposal.entityId === editingNote.id
      ? timingProposal
      : null;

  const poiOptions = useMemo(
    () => [
      { value: "", label: "None" },
      ...[...pois]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((poi) => ({ value: poi.id, label: poi.name })),
    ],
    [pois],
  );

  const readOnly = isReadOnly();

  const handleSave = () => {
    if (!name.trim()) {
      setError("Give the note a name.");
      return;
    }

    if (allDay) {
      if (!allDayStartDate || !allDayEndDate) {
        setError("Set both the start and end date.");
        return;
      }
      if (allDayEndDate < allDayStartDate) {
        setError("The end date must be on or after the start date.");
        return;
      }
      if (
        trip &&
        (allDayStartDate < trip.startDate || allDayEndDate > trip.endDate)
      ) {
        setError("All-day notes must stay within the trip dates.");
        return;
      }
    }

    const hasStart = !allDay && !!(start.date && start.time);
    const hasEnd = !allDay && !!(end.date && end.time);
    if (hasStart !== hasEnd) {
      setError("Set both start and end times, or neither.");
      return;
    }
    const startFieldError = allDay ? null : eventTimeFieldsError(start);
    const endFieldError = allDay ? null : eventTimeFieldsError(end);
    if (startFieldError || endFieldError) {
      setError(startFieldError ?? endFieldError);
      return;
    }
    const startTime = hasStart ? zonedFromEventTimeFields(start) : null;
    const endTime = hasEnd ? zonedFromEventTimeFields(end) : null;
    if ((hasStart && !startTime) || (hasEnd && !endTime)) {
      setError("Choose valid start and end wall times.");
      return;
    }
    if (startTime && endTime && epochMs(endTime) <= epochMs(startTime)) {
      setError("The end time must be after the start time.");
      return;
    }

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      startTime,
      endTime,
      allDayStartDate: allDay ? allDayStartDate : null,
      allDayEndDate: allDay ? allDayEndDate : null,
      tagIds,
      startPoiId: startPoiId || null,
      endPoiId: endPoiId || null,
      locked,
      legIds,
    };

    if (isCreate) {
      createNote(payload);
    } else if (editingNote) {
      updateNote(editingNote.id, payload);
    }
    closeEditor();
  };

  const handleDelete = () => {
    if (!editingNote) return;
    deleteNote(editingNote.id);
    closeEditor();
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeEditor}
      title={isCreate ? "New note" : "Edit note"}
      description="Edit note details, timing, route, and tags."
      icon="sticky_note_2"
      size="xl"
      footer={
        <>
          {!isCreate && editingNote && !isReadOnly() && (
            <Button
              variant="danger"
              icon="delete"
              onClick={handleDelete}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button onClick={closeEditor} variant="secondary">
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} variant="primary">
              Save note
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {activeTimingProposal && (
          <TimezoneChangeNotice proposal={activeTimingProposal} />
        )}
        <EventEditorSection
          id="note-details-heading"
          title="Note details"
          description="The calendar label and practical context."
          icon="edit_note"
        >
          <div className="grid gap-4 rounded-xl border border-border bg-surface p-4 shadow-sm">
            <FormField label="Name">
              <FormInput
                value={name}
                onChange={setName}
                placeholder="e.g. Shinkansen NOZOMI 214"
                autoFocus
                disabled={readOnly}
              />
            </FormField>
            <FormField label="Description">
              <FormTextarea
                value={description}
                onChange={setDescription}
                placeholder="Booking reference, seats, platform…"
                disabled={readOnly}
              />
            </FormField>
          </div>
        </EventEditorSection>

        <EventEditorSection
          id="note-timing-heading"
          title="Timing"
          description={
            allDay
              ? "Shown throughout every selected day."
              : "Leave both times empty to keep this note in the inbox."
          }
          icon="schedule"
        >
          <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
            <label className="flex w-fit cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm font-semibold text-primary has-[:disabled]:cursor-default has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-info">
              <input
                type="checkbox"
                checked={allDay}
                disabled={readOnly}
                onChange={(event) => {
                  const next = event.target.checked;
                  setAllDay(next);
                  if (!next) {
                    if (timedDraftBeforeAllDay.current) {
                      setStart(timedDraftBeforeAllDay.current.start);
                      setEnd(timedDraftBeforeAllDay.current.end);
                    }
                    return;
                  }
                  timedDraftBeforeAllDay.current = { start, end };
                  const fallbackDate =
                    allDayStartDate ||
                    start.date ||
                    editingNote?.startTime?.slice(0, 10) ||
                    trip?.startDate ||
                    "";
                  setAllDayStartDate(fallbackDate);
                  setAllDayEndDate(allDayEndDate || fallbackDate);
                  setStart(emptyTimeFields(start.zone || defaultZone));
                  setEnd(emptyTimeFields(end.zone || defaultZone));
                }}
                className="size-4 accent-[var(--color-calendar-all-day-border)]"
              />
              <span
                className="material-symbols-outlined text-base leading-none text-calendar-all-day-text"
                aria-hidden
              >
                calendar_today
              </span>
              All day
            </label>

            {allDay ? (
              <FormRow>
                <FormField label="Start date" flex={1}>
                  <FormInput
                    type="date"
                    value={allDayStartDate}
                    onChange={setAllDayStartDate}
                    min={trip?.startDate}
                    disabled={readOnly}
                  />
                </FormField>
                <FormField label="End date" flex={1}>
                  <FormInput
                    type="date"
                    value={allDayEndDate}
                    onChange={setAllDayEndDate}
                    min={allDayStartDate || trip?.startDate}
                    disabled={readOnly}
                  />
                </FormField>
              </FormRow>
            ) : (
              <div className="rounded-xl border border-border bg-subtle p-1.5">
                <EventTimingFields
                  start={start}
                  end={end}
                  onStartChange={setStart}
                  onEndChange={setEnd}
                  disabled={readOnly}
                />
              </div>
            )}
          </div>
          <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg px-1 text-sm font-semibold text-secondary has-[:disabled]:cursor-default">
            <input
              type="checkbox"
              checked={locked}
              onChange={(event) => setLocked(event.target.checked)}
              disabled={readOnly}
              className="size-4 accent-[var(--color-indigo-500)]"
            />
            <span
              className="material-symbols-outlined text-base leading-none"
              aria-hidden
            >
              lock
            </span>
            {allDay
              ? "Lock dates (prevents date changes)"
              : "Lock time (booked — can't be moved or resized)"}
          </label>
        </EventEditorSection>

        <EventEditorSection
          id="note-route-heading"
          title="Route"
          description="Optional places clarify where this note starts and ends."
          icon="alt_route"
        >
          <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
            <FormRow>
              <FormField label="From (optional)" flex={1}>
                <FormSelect
                  value={startPoiId}
                  onChange={setStartPoiId}
                  options={poiOptions}
                  disabled={readOnly}
                />
              </FormField>
              <FormField label="To (optional)" flex={1}>
                <FormSelect
                  value={endPoiId}
                  onChange={setEndPoiId}
                  options={poiOptions}
                  disabled={readOnly}
                />
              </FormField>
            </FormRow>
          </div>
        </EventEditorSection>

        <EventEditorSection
          id="note-legs-heading"
          title="Legs"
          description="Assign this note to the parts of the trip it belongs to. Legs may overlap."
          icon="route"
        >
          <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
            <NoteLegChips
              selectedIds={legIds}
              onChange={setLegIds}
              disabled={readOnly}
            />
          </div>
        </EventEditorSection>

        <EventEditorSection
          id="note-tags-heading"
          title="Tags"
          description="Relevant tags are shown first; search reaches the full library."
          icon="label"
        >
          <NoteTagPicker
            tags={tags}
            notes={notes}
            selectedIds={tagIds}
            onChange={setTagIds}
            disabled={readOnly}
          />
        </EventEditorSection>

        {error && <EventEditorAlert>{error}</EventEditorAlert>}
      </div>
    </Modal>
  );
}
